import {Component, inject, Input, OnInit, ViewChild} from '@angular/core';
import {MatTable} from '@angular/material/table';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {IApiKeyOptions, IClientOptions} from "../../../models/client-options.model";
import {ToastService} from "../../../services/layout/toast.service";
import {ConfirmDialogService} from "../../../services/layout/dialog.service";
import {MaterialModule} from "../../../material.module";
import {DatePipe} from "@angular/common";
import {ShineApiKeyService} from "../../../services/auth/shine-auth/shine-api-key.service";
import {of} from "rxjs";
import {catchError, finalize} from "rxjs/operators";

@Component({
  selector: 'app-api-keys',
  templateUrl: './api-keys.component.html',
  styleUrls: ['./api-keys.component.scss'],
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    DatePipe,
  ],
  standalone: true
})
export class ApiKeysComponent implements OnInit {
    private apiKeyService = inject(ShineApiKeyService);
    private toastService = inject(ToastService);
    private dialogService = inject(ConfirmDialogService);
    private fb = inject(FormBuilder);

  @Input() client: IClientOptions;
  isAdding = false;
  isLoading = true;

  // Forms
  addKeyForm: FormGroup;

  // Data
  apiKeys: IApiKeyOptions[] = [];

  // Tables
  @ViewChild('apiKeysTable') apiKeysTable!: MatTable<any>;

  // Column definitions
    apiKeysColumns: string[] = ['name', 'permissions', 'createdAt', 'expiresAt', 'status', 'actions'];

    constructor() {
    // Initialize the add API key form with validation
    this.addKeyForm = this.fb.group({
      keyName: ['', [Validators.required, Validators.maxLength(50)]]
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  /**
   * Loads all data needed for the component
   */
  loadData(): void {
    this.isLoading = true;

      this.apiKeyService.getApiKeys(this.client.id).pipe(
        catchError(error => {
            console.error('Error loading API keys:', error);
            this.toastService.showError('Error loading API keys');
            return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
        })
    ).subscribe(result => {
          this.apiKeys = result ?? [];
          this.renderApiKeysTable();
      });
  }

  /**
   * Removes an API key
   * @param id The ID of the API key to remove
   */
  removeKey(id: string): void {
    this.dialogService.confirm({
      title: 'Remove API Key',
      message: 'Are you sure you want to remove this API key? This action cannot be undone.',
      info: '',
      confirmButtonText: 'Remove',
      cancelButtonText: 'Cancel'
    }).subscribe(confirmed => {
      if (confirmed) {
          this.apiKeyService.deleteApiKey(id, this.client.id).subscribe(
            success => {
              if (success) {
                this.toastService.showSuccess('API Key removed successfully');

                  // Reload data to refresh API keys
                this.loadData();
              } else {
                this.toastService.showError('Error removing API Key');
              }
            },
            error => {
              console.error('Remove Key Error:', error);
              this.toastService.showError('Error removing API Key');
            }
        );
      }
    });
  }

  /**
   * Adds a new API key
   */
  addKey(): void {
    if (this.addKeyForm.invalid) {
      this.toastService.showError('Please enter a valid API Key name.');
      return;
    }

    const keyName = this.addKeyForm.get('keyName')?.value.trim();
    if (!keyName) {
      this.toastService.showError('API Key name cannot be empty.');
      return;
    }

    // Check for duplicates
    this.apiKeyService.checkDuplicate(this.client.id, keyName).subscribe(
        isDuplicate => {
          if (isDuplicate) {
            this.toastService.showError(`An API key with the name "${keyName}" already exists.`);
            return;
          }

          // Proceed with creating the key
          this.createApiKey(keyName);
        },
        error => {
          console.error('Check Duplicate Error:', error);
          // Proceed anyway if we can't check for duplicates
          this.createApiKey(keyName);
        }
    );
  }

  /**
   * Gets a human-friendly status label for an API key.
   */
  getStatusLabel(apiKey: IApiKeyOptions): string {
      const status = this.getKeyStatus(apiKey);
      if (status === 'expired') return 'Expired';
      if (status === 'inactive') return 'Inactive';
      return 'Active';
  }

  /**
   * Gets the status color for an API key.
   */
  getStatusColor(apiKey: IApiKeyOptions): string {
      switch (this.getKeyStatus(apiKey)) {
          case 'active':
              return '#2e7d32';
          case 'expired':
              return '#c62828';
          case 'inactive':
      default:
          return '#6b7280';
      }
  }

    /**
     * Formats permissions for display.
     */
    getPermissionsLabel(permissions?: string[]): string {
        if (!permissions || permissions.length === 0) return 'Default';
        return permissions.join(', ');
    }

    private getKeyStatus(apiKey: IApiKeyOptions): 'active' | 'inactive' | 'expired' {
        if (this.isExpired(apiKey.expiresAt)) return 'expired';
        if (apiKey.isActive === false) return 'inactive';
        return 'active';
    }

    private isExpired(expiresAt?: string): boolean {
        if (!expiresAt) return false;
        const parsed = new Date(expiresAt);
        if (Number.isNaN(parsed.getTime())) return false;
        return parsed.getTime() < Date.now();
  }

  /**
   * Creates a new API key
   * @param keyName The name of the API key to create
   */
  private createApiKey(keyName: string): void {
    this.isAdding = true;

      this.apiKeyService.createApiKey({
      name: keyName,
          clientId: this.client.id
      }).subscribe(
        newKey => {
          // Show confirmation dialog with the new API key
          this.dialogService.confirm({
            title: 'This is your new API key',
            message: newKey.key,
            info: 'Store the key securely as you will not be able to see it again.'
          });

          this.addKeyForm.reset();
          this.toastService.showSuccess('API Key added successfully');

            // Reload data to refresh API keys
          this.loadData();
        },
        error => {
          console.error('Add Key Error:', error);
          this.toastService.showError('Error adding API Key');
        }
    ).add(() => {
      this.isAdding = false;
    });
  }

    private renderApiKeysTable(): void {
        if (this.apiKeysTable) {
            this.apiKeysTable.renderRows();
        }
    }
}
